import { useCallback, useMemo, useState } from "react";

interface EditorModalState<T> {
  /** The item being edited, or null when adding a new one */
  item: T | null;
  /** Changes on every open, so the form starts from the item again */
  key: number;
  open: boolean;
}

/**
 * State for a modal that edits an item or adds a new one. The modal stays mounted after closing so it can
 * animate out; render it while `state` is set.
 */
export function useEditorModal<T>() {
  const [state, setState] = useState<EditorModalState<T> | null>(null);
  const show = useCallback(
    (item: T | null) =>
      setState((s) => ({ item, key: (s?.key ?? 0) + 1, open: true })),
    [],
  );
  const edit = useCallback((item: T) => show(item), [show]);
  const add = useCallback(() => show(null), [show]);
  const close = useCallback(
    () => setState((s) => s && { ...s, open: false }),
    [],
  );
  return useMemo(
    () => ({ state, edit, add, close }),
    [state, edit, add, close],
  );
}
